import type { AiModelsCatalogClient } from "../catalog/AiModelsCatalogClient.js";
import { LOCAL_PROVIDERS } from "../sync/modelNameResolver/constants.js";
import {
  loadOpenRouterRoutingEnv,
  shouldDefaultRouteViaOpenRouter,
} from "../sync/openRouterRoutingEnv.js";
import { normalizeProvider, normalizeString } from "../sync/modelNameResolver/normalize.js";
import type {
  ModelResolutionInput,
  ModelResolutionResult,
  ModelResolverOptions,
} from "../sync/modelNameResolver/types.js";
import type { OpenRouterRoutingConfig } from "../sync/openRouterRoutingEnv.js";

export function isOpenRouterProvider(provider?: string): boolean {
  return normalizeProvider(provider) === "openrouter";
}

export function isLocalProvider(provider?: string): boolean {
  const p = normalizeProvider(provider) ?? normalizeString(provider ?? "");
  return LOCAL_PROVIDERS.has(p);
}

/** Catalog resolver returned success without a priced record (Ollama, LM Studio, …). */
export function isLocalProviderResolution(
  resolved: ModelResolutionResult | null,
  inputProvider?: string,
): boolean {
  if (!resolved?.found || resolved.record) return false;
  if (resolved.resolvedVia.includes("local-provider-passthrough")) return true;
  return isLocalProvider(inputProvider);
}

/**
 * Vendor id for a model slug (`deepseek/deepseek-v3.2` → `deepseek`).
 * Ignores `openrouter` as a provider hint — it is a routing layer, not the model owner.
 */
export function underlyingProviderFromModel(
  model: string,
  providerHint?: string,
): string | undefined {
  const normalized = normalizeString(model);
  if (normalized.includes("/")) {
    const prefix = normalized.split("/")[0]!;
    if (!isOpenRouterProvider(prefix)) return prefix;
  }
  if (providerHint && !isOpenRouterProvider(providerHint)) {
    return normalizeProvider(providerHint);
  }
  return undefined;
}

/** Provider hint for ai-profiles index lookup (openrouter → underlying vendor). */
export function providerHintForProfiles(
  model: string,
  provider?: string,
): string | undefined {
  if (isOpenRouterProvider(provider)) {
    return underlyingProviderFromModel(model, provider);
  }
  return normalizeProvider(provider);
}

/**
 * Catalog resolve attempts for direct API and OpenRouter usage.
 * Tries the caller hint, inferred vendor, no hint, and openrouter routing.
 */
export function buildCatalogResolveAttempts(
  model: string,
  provider?: string,
): ModelResolutionInput[] {
  const attempts: ModelResolutionInput[] = [];
  const seen = new Set<string>();
  const add = (m: string, p?: string) => {
    const key = `${p ?? ""}\0${normalizeString(m)}`;
    if (seen.has(key)) return;
    seen.add(key);
    attempts.push({ model: m, provider: p });
  };

  add(model, provider);

  const underlying = underlyingProviderFromModel(model, provider);
  if (provider) add(model, undefined);
  if (underlying) add(model, underlying);
  if (!isOpenRouterProvider(provider)) {
    add(model, "openrouter");
  }

  return attempts;
}

export async function resolveFromCatalogAttempts(
  catalog: AiModelsCatalogClient,
  attempts: ModelResolutionInput[],
  options?: ModelResolverOptions,
): Promise<ModelResolutionResult | null> {
  let last: ModelResolutionResult | null = null;
  let localPassthrough: ModelResolutionResult | null = null;

  for (const attempt of attempts) {
    const result = await catalog.resolveModel(attempt, options);
    last = result;
    if (result.found && result.record) return result;
    if (
      result.found &&
      !result.record &&
      isLocalProviderResolution(result, attempt.provider)
    ) {
      localPassthrough = result;
    }
  }

  return localPassthrough ?? last;
}

export function resolveRoutedViaOpenRouter(
  inputProvider: string | undefined,
  resolved: ModelResolutionResult | null,
  modelId?: string,
  routingEnv?: OpenRouterRoutingConfig,
): boolean {
  const env = routingEnv ?? loadOpenRouterRoutingEnv();

  if (isOpenRouterProvider(inputProvider)) return true;

  if (resolved?.found) {
    return resolved.routedViaOpenRouter;
  }

  const vendor = underlyingProviderFromModel(
    modelId ?? (resolved?.found ? resolved.modelId : "") ?? "",
    inputProvider,
  );
  if (vendor && shouldDefaultRouteViaOpenRouter(vendor, env)) {
    return true;
  }

  return false;
}
